import { getFontCss, Popup, styleConstants } from '@paywithextend/components'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { matchPath } from 'react-router'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import ConfirmationPopupContent from 'src/components/ConfirmationPopupContent/ConfirmationPopupContent'
import NotFoundPopupContent from 'src/components/NotFoundPopupContent/NotFoundPopupContent'
import { ExtendCurrency } from 'src/enums'
import Crncy from 'src/localization/Crncy'
import { useTranslate } from 'src/localization/Trns'
import { expensesOutOfPocketReportsUrl } from 'src/navigation/urls'
import { createSetIsMarkExpenseReportsPaidPopupOpen } from 'src/redux/actions/app.actions'
import { postMarkExpenseReportsPaid } from 'src/redux/actions/data/expenses.actions'
import {
  selectPostMarkingExpenseReportPaid,
  selectPostMarkingExpenseReportPaidError,
  selectSelectedMarkExpensesPaidAmountFromState,
  selectSelectedMarkExpensesPaidFromState,
} from 'src/redux/selectors/data/expenses.selectors'
import { selectCalculatedBackgroundLocation } from 'src/redux/selectors/router.selectors'
import { toDollars } from 'src/utils/currency.utils'
import { css } from 'styled-components'

const MarkExpenseReportsPaidPopup: React.FC<
  React.PropsWithChildren
> = (): React.ReactElement => {
  const translate = useTranslate()

  const dispatch = useDispatch()

  const expenseReports = useSelector(selectSelectedMarkExpensesPaidFromState)
  const expenseReportsAmount = useSelector(
    selectSelectedMarkExpensesPaidAmountFromState,
  )
  const loading = useSelector(selectPostMarkingExpenseReportPaid)
  const error = useSelector(selectPostMarkingExpenseReportPaidError)
  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )
  const allReports =
    matchPath(
      calculatedBackgroundLocation.pathname,
      expensesOutOfPocketReportsUrl(),
    ) !== null

  const { analytics } = useAnalyticsContext()

  const subTitleTopText = expenseReports !== undefined &&
    expenseReportsAmount !== undefined && (
      <>
        {allReports
          ? expenseReports.length === 1
            ? translate('You are about to mark 1 report as paid.')
            : translate('You are about to mark ${count} reports as paid.', {
                count: expenseReports.length.toString(),
              })
          : translate('You are about to mark this report as paid.')}
        {translate(' The total approved amount is ${amount}.', {
          amount: (
            <Crncy
              currency={ExtendCurrency.Usd}
              amount={toDollars(expenseReportsAmount)}
            />
          ),
        })}
      </>
    )

  const closePopup = (): void => {
    dispatch(createSetIsMarkExpenseReportsPaidPopupOpen(false))
  }

  return (
    <Popup
      onClickClose={closePopup}
      showCloseButton
      css={css`
        display: flex;
        position: relative;
      `}
    >
      {expenseReports !== undefined && expenseReportsAmount !== undefined ? (
        <ConfirmationPopupContent
          title={
            expenseReports.length === 1
              ? translate('Mark Report as Paid')
              : translate('Mark Reports as Paid')
          }
          subtitle={
            <>
              <p
                css={css`
                  margin-bottom: 20px;
                `}
              >
                {subTitleTopText}
              </p>
              <p
                css={css`
                  margin-bottom: 20px;
                `}
              >
                {translate(
                  'Once you have marked any reports as paid, the status will be changed permanently from "Reviewed" to "Paid".',
                )}
              </p>
              <p
                css={css`
                  ${getFontCss(
                    16,
                    styleConstants.colors.black,
                    styleConstants.fontFamilies.medium,
                  )};
                `}
              >
                {translate('This action cannot be undone.')}
              </p>
            </>
          }
          actionButtonLabel={translate('Confirm and Mark as Paid')}
          actionButtonOnClick={(): void => {
            dispatch(
              postMarkExpenseReportsPaid(expenseReports, allReports, analytics),
            )
          }}
          actionButtonLoading={loading}
          actionButtonVariant="primary"
          isCancelButtonLink
          cancelButtonLabel={translate('Cancel')}
          cancelButtonOnClick={closePopup}
          topLevelError={error}
          underline={false}
        />
      ) : (
        <NotFoundPopupContent />
      )}
    </Popup>
  )
}

export default MarkExpenseReportsPaidPopup
